import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Button,
  ButtonGroup,
  Card,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  PieChart,
  Pie,
  Legend,
} from "recharts";

const COLORS = [
  "#69DB7C",
  "#4DABF7",
  "#FFA94D",
  "#FF6B6B",
  "#9775FA",
  "#F783AC",
  "#868E96",
];

const filterOptions = ["daily", "weekly", "monthly", "yearly"];
const filterLabels = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
};

const tableLabels = {
  ca_orders: "CA Orders",
  dallas_orders: "Dallas Orders",
  houston_orders: "Houston Orders",
  okc_orders: "OKC Orders",
  orders: "General Orders",
};

const sourceTableColors = {
  orders: "#69DB7C",
  okc_orders: "#4DABF7",
  ca_orders: "#FFA94D",
  dallas_orders: "#FF6B6B",
  houston_orders: "#9775FA",
};

const ProductionStatsPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedFilter, setSelectedFilter] = useState("daily");
  const [mayaPrinterData, setMayaPrinterData] = useState([]);
  const [jiffyPrinterData, setJiffyPrinterData] = useState([]);
  const [jiffyTableStats, setJiffyTableStats] = useState([]);
  const [jiffyOrderTypeStats, setJiffyOrderTypeStats] = useState([]);
  const [jiffyPrintTypeStats, setJiffyPrintTypeStats] = useState([]);
  const [mayaOrderStats, setMayaOrderStats] = useState({
    cancelled: 0,
    deleted: 0,
  });
  const [jiffyOrderStats, setJiffyOrderStats] = useState({
    cancelled: 0,
    deleted: 0,
    total_inches: 0,
    total_orders: 0,
  });
  const [loading, setLoading] = useState({ maya: false, jiffy: false });
  const [error, setError] = useState({ maya: null, jiffy: null });

  // Format date for PHP API
  const formatDateForAPI = (date) => {
    return date.toISOString().split("T")[0];
  };

  // Fetch Maya data from PHP API
  const fetchMayaProductionData = async () => {
    setLoading((prev) => ({ ...prev, maya: true }));
    setError((prev) => ({ ...prev, maya: null }));

    try {
      const dateParam = formatDateForAPI(selectedDate);
      const response = await fetch(
        `https://mayatxupload.com/mayaUp/getPrinterInchStats.php?date=${dateParam}&filter=${selectedFilter}`
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.error) throw new Error(data.error);

      const transformedPrinterData = data.printers.map((printer) => ({
        name: printer.printer_name,
        value: parseFloat(printer.total_inches) || 0,
        order_count: parseInt(printer.order_count) || 0,
      }));

      setMayaPrinterData(transformedPrinterData);
      setMayaOrderStats({
        cancelled: data.cancelled || 0,
        deleted: data.deleted || 0,
      });
    } catch (err) {
      console.error("Error fetching Maya production data:", err);
      setError((prev) => ({ ...prev, maya: err.message }));
      setMayaPrinterData([]);
      setMayaOrderStats({ cancelled: 0, deleted: 0 });
    } finally {
      setLoading((prev) => ({ ...prev, maya: false }));
    }
  };

  // Fetch Jiffy data from PHP API
  const fetchJiffyProductionData = async () => {
    setLoading((prev) => ({ ...prev, jiffy: true }));
    setError((prev) => ({ ...prev, jiffy: null }));

    try {
      const dateParam = formatDateForAPI(selectedDate);
      const response = await fetch(
        `https://mayatxupload.com/mayaUp/getJiffyPrinterInchStats.php?date=${dateParam}&filter=${selectedFilter}`
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.error) throw new Error(data.error);

      // Transform printer data for chart - source_table bilgisi korunuyor
      const transformedPrinterData = data.printers.map((printer) => ({
        name: printer.printer_name,
        value: parseFloat(printer.total_inches) || 0,
        order_count: parseInt(printer.order_count) || 0,
        source_table: printer.source_table,
      }));

      // Transform table stats for pie chart
      const transformedTableStats = data.table_stats.map((table) => ({
        name: tableLabels[table.source_table] || table.source_table,
        value: parseFloat(table.table_total_inches) || 0,
        order_count: parseInt(table.order_count) || 0,
        source_table: table.source_table,
      }));

      // Transform order type stats for chart - source_table bilgisi korunuyor
      const transformedOrderTypeStats = data.order_type_stats.map(
        (orderType) => ({
          order_type: orderType.order_type,
          total_inches: parseFloat(orderType.total_inches) || 0,
          order_count: parseInt(orderType.order_count) || 0,
          source_table: orderType.source_table,
        })
      );

      // Transform print type stats for chart - source_table bilgisi korunuyor
      const transformedPrintTypeStats = data.print_type_stats.map(
        (printType) => ({
          name: printType.print_type,
          value: parseFloat(printType.total_inches) || 0,
          source_table: printType.source_table,
        })
      );

      setJiffyPrinterData(transformedPrinterData);
      setJiffyTableStats(transformedTableStats);
      setJiffyOrderTypeStats(transformedOrderTypeStats);
      setJiffyPrintTypeStats(transformedPrintTypeStats);
      setJiffyOrderStats({
        cancelled: data.cancelled || 0,
        deleted: data.deleted || 0,
        total_inches: parseFloat(data.total_inches) || 0,
        total_orders: parseInt(data.total_orders) || 0,
      });
    } catch (err) {
      console.error("Error fetching Jiffy production data:", err);
      setError((prev) => ({ ...prev, jiffy: err.message }));
      setJiffyPrinterData([]);
      setJiffyTableStats([]);
      setJiffyOrderTypeStats([]);
      setJiffyPrintTypeStats([]);
      setJiffyOrderStats({
        cancelled: 0,
        deleted: 0,
        total_inches: 0,
        total_orders: 0,
      });
    } finally {
      setLoading((prev) => ({ ...prev, jiffy: false }));
    }
  };

  useEffect(() => {
    fetchMayaProductionData();
    fetchJiffyProductionData();
  }, [selectedDate, selectedFilter]);

  // Source table badge component
  const SourceTableBadge = ({ sourceTable }) => {
    const getTableLabel = (table) => {
      return tableLabels[table] || table;
    };

    const getTableColor = (table) => {
      return sourceTableColors[table] || "#868E96";
    };

    if (Array.isArray(sourceTable)) {
      return (
        <div
          style={{
            display: "flex",
            gap: "4px",
            flexWrap: "wrap",
            marginTop: "5px",
          }}
        >
          {sourceTable.map((table, index) => (
            <Badge
              key={index}
              bg="light"
              text="dark"
              style={{
                fontSize: "10px",
                backgroundColor: getTableColor(table),
                color: "white",
              }}
            >
              {getTableLabel(table)}
            </Badge>
          ))}
        </div>
      );
    }

    return (
      <Badge
        bg="light"
        text="dark"
        style={{
          fontSize: "10px",
          backgroundColor: getTableColor(sourceTable),
          color: "white",
          marginTop: "5px",
        }}
      >
        {getTableLabel(sourceTable)}
      </Badge>
    );
  };

  // Common printer chart component - source_table bilgisi eklendi
  const PrinterChart = ({ data, loading, error }) => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div>Loading printer data...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
            border: "1px solid #f5c6cb",
          }}
        >
          Error: {error}
        </div>
      );
    }

    if (data.length > 0) {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 20, right: 40, left: 0, bottom: 0 }}
          >
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === "value") return [`${value} inches`, "Inches"];
                if (name === "order_count")
                  return [`${value} orders`, "Orders"];
                return [value, name];
              }}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div
                      className="custom-tooltip"
                      style={{
                        backgroundColor: "#fff",
                        padding: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    >
                      <p style={{ fontWeight: "bold", margin: 0 }}>{label}</p>
                      <p style={{ margin: "5px 0" }}>
                        Inches: {payload[0].value}
                      </p>
                      <p style={{ margin: "5px 0" }}>
                        Orders: {data.order_count}
                      </p>
                      {data.source_table && (
                        <p style={{ margin: "5px 0", fontSize: "12px" }}>
                          Source:{" "}
                          {Array.isArray(data.source_table)
                            ? data.source_table
                                .map((table) => tableLabels[table] || table)
                                .join(", ")
                            : tableLabels[data.source_table] ||
                              data.source_table}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="value" barSize={24}>
              <LabelList
                dataKey="value"
                position="right"
                formatter={(val) => `${val} in`}
              />
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: "40px",
          borderRadius: "8px",
          textAlign: "center",
          color: "#666",
          boxShadow: "0 0 6px rgba(0,0,0,0.05)",
        }}
      >
        No printer data found for selected period
      </div>
    );
  };

  // Table distribution pie chart
  const TableDistributionChart = ({ data, loading, error }) => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div>Loading table distribution...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
            border: "1px solid #f5c6cb",
          }}
        >
          Error: {error}
        </div>
      );
    }

    if (data.length > 0) {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent, order_count }) =>
                `${name}\n${(percent * 100).toFixed(
                  0
                )}% (${order_count} orders)`
              }
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    sourceTableColors[entry.source_table] ||
                    COLORS[index % COLORS.length]
                  }
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => {
                if (name === "value") return [`${value} inches`, "Inches"];
                return [value, name];
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: "40px",
          borderRadius: "8px",
          textAlign: "center",
          color: "#666",
          boxShadow: "0 0 6px rgba(0,0,0,0.05)",
        }}
      >
        No table distribution data found
      </div>
    );
  };

  // Order type chart component - source_table bilgisi eklendi
  const OrderTypeChart = ({ data, loading, error }) => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div>Loading order types...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
            border: "1px solid #f5c6cb",
          }}
        >
          Error: {error}
        </div>
      );
    }

    if (data.length > 0) {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 20, right: 40, left: 0, bottom: 0 }}
          >
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="order_type"
              width={140}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value, name) => {
                if (name === "total_inches")
                  return [`${value} inches`, "Inches"];
                if (name === "order_count")
                  return [`${value} orders`, "Orders"];
                return [value, name];
              }}
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div
                      className="custom-tooltip"
                      style={{
                        backgroundColor: "#fff",
                        padding: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    >
                      <p style={{ fontWeight: "bold", margin: 0 }}>{label}</p>
                      <p style={{ margin: "5px 0" }}>
                        Inches: {payload[0].value}
                      </p>
                      <p style={{ margin: "5px 0" }}>
                        Orders: {data.order_count}
                      </p>
                      {data.source_table && (
                        <p style={{ margin: "5px 0", fontSize: "12px" }}>
                          Source:{" "}
                          {tableLabels[data.source_table] || data.source_table}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="total_inches" barSize={24}>
              <LabelList
                dataKey="total_inches"
                position="right"
                formatter={(val) => `${val} in`}
              />
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    sourceTableColors[entry.source_table] ||
                    COLORS[index % COLORS.length]
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: "40px",
          borderRadius: "8px",
          textAlign: "center",
          color: "#666",
          boxShadow: "0 0 6px rgba(0,0,0,0.05)",
        }}
      >
        No order type data found for selected period
      </div>
    );
  };

  // Print type chart component - source_table bilgisi eklendi
  const PrintTypeChart = ({ data, loading, error }) => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div>Loading print types...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div
          style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
            border: "1px solid #f5c6cb",
          }}
        >
          Error: {error}
        </div>
      );
    }

    if (data.length > 0) {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent, source_table }) =>
                `${name}\n${(percent * 100).toFixed(0)}%`
              }
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    sourceTableColors[entry.source_table] ||
                    COLORS[index % COLORS.length]
                  }
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => `${value} inches`}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div
                      className="custom-tooltip"
                      style={{
                        backgroundColor: "#fff",
                        padding: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    >
                      <p style={{ fontWeight: "bold", margin: 0 }}>
                        {data.name}
                      </p>
                      <p style={{ margin: "5px 0" }}>Inches: {data.value}</p>
                      {data.source_table && (
                        <p style={{ margin: "5px 0", fontSize: "12px" }}>
                          Source:{" "}
                          {tableLabels[data.source_table] || data.source_table}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: "40px",
          borderRadius: "8px",
          textAlign: "center",
          color: "#666",
          boxShadow: "0 0 6px rgba(0,0,0,0.05)",
        }}
      >
        No print type data found for selected period
      </div>
    );
  };

  // Common statistics summary component
  const StatisticsSummary = ({
    printerData,
    orderStats,
    isJiffy = false,
    tableStats = [],
    orderTypeStats = [],
    printTypeStats = [],
  }) => {
    const totalInches = isJiffy
      ? orderStats.total_inches
      : printerData.reduce((sum, printer) => sum + printer.value, 0);

    const totalOrders = isJiffy
      ? orderStats.total_orders
      : printerData.reduce(
          (sum, printer) => sum + (printer.order_count || 0),
          0
        );

    return (
      <div style={{ marginTop: "30px" }}>
        <h5 className="mb-3">📊 Statistics Summary</h5>
        <Row>
          <Col md={isJiffy ? 6 : 12}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: isJiffy ? "20px" : "0",
              }}
            >
              <Card className="text-center">
                <Card.Body>
                  <Card.Title
                    style={{
                      color: "#69DB7C",
                      fontSize: "14px",
                      marginBottom: "10px",
                    }}
                  >
                    🖨️ Active Printers
                  </Card.Title>
                  <Card.Text style={{ fontSize: "24px", fontWeight: "bold" }}>
                    {printerData.length}
                  </Card.Text>
                </Card.Body>
              </Card>

              <Card className="text-center">
                <Card.Body>
                  <Card.Title
                    style={{
                      color: "#4DABF7",
                      fontSize: "14px",
                      marginBottom: "10px",
                    }}
                  >
                    📏 Total {isJiffy ? "Jiffy" : "DTF"} Inches
                  </Card.Title>
                  <Card.Text style={{ fontSize: "24px", fontWeight: "bold" }}>
                    {totalInches.toLocaleString()}
                  </Card.Text>
                </Card.Body>
              </Card>

              <Card className="text-center">
                <Card.Body>
                  <Card.Title
                    style={{
                      color: "#9775FA",
                      fontSize: "14px",
                      marginBottom: "10px",
                    }}
                  >
                    📦 Total Orders
                  </Card.Title>
                  <Card.Text style={{ fontSize: "24px", fontWeight: "bold" }}>
                    {totalOrders.toLocaleString()}
                  </Card.Text>
                </Card.Body>
              </Card>

              <Card className="text-center">
                <Card.Body>
                  <Card.Title
                    style={{
                      color: "#FF6B6B",
                      fontSize: "14px",
                      marginBottom: "10px",
                    }}
                  >
                    🚫 Cancelled
                  </Card.Title>
                  <Card.Text style={{ fontSize: "24px", fontWeight: "bold" }}>
                    {orderStats.cancelled}
                  </Card.Text>
                </Card.Body>
              </Card>

              <Card className="text-center">
                <Card.Body>
                  <Card.Title
                    style={{
                      color: "#FFA94D",
                      fontSize: "14px",
                      marginBottom: "10px",
                    }}
                  >
                    🗑️ Deleted
                  </Card.Title>
                  <Card.Text style={{ fontSize: "24px", fontWeight: "bold" }}>
                    {orderStats.deleted}
                  </Card.Text>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {isJiffy && orderTypeStats.length > 0 && (
            <Col md={6}>
              <Card>
                <Card.Body>
                  <Card.Title
                    style={{ fontSize: "16px", marginBottom: "15px" }}
                  >
                    📦 Order Type Distribution
                  </Card.Title>
                  <OrderTypeChart data={orderTypeStats} />
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>

        {isJiffy && (tableStats.length > 0 || printTypeStats.length > 0) && (
          <Row className="mt-3">
            {tableStats.length > 0 && (
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title
                      style={{ fontSize: "16px", marginBottom: "15px" }}
                    >
                      📋 Order Distribution by Location
                    </Card.Title>
                    <TableDistributionChart data={tableStats} />
                  </Card.Body>
                </Card>
              </Col>
            )}

            {printTypeStats.length > 0 && (
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Card.Title
                      style={{ fontSize: "16px", marginBottom: "15px" }}
                    >
                      🎨 Print Type Distribution
                    </Card.Title>
                    <PrintTypeChart data={printTypeStats} />
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        padding: "30px",
        fontFamily: "Segoe UI, sans-serif",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      {/* Top Header */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <Button variant="outline-secondary" href="/home">
          🏠 Home
        </Button>
      </div>

      {/* Page Title */}
      <div style={{ textAlign: "center", marginBottom: "30px" }}>
        <h1 style={{ color: "#333", fontWeight: "bold" }}>
          Production Statistics
        </h1>
        <p style={{ color: "#666" }}>
          Monitor your printing production across all systems
        </p>
      </div>

      {/* Date & Filter Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "15px",
        }}
      >
        <div style={{ minWidth: "220px" }}>
          <label
            style={{
              fontWeight: "bold",
              marginBottom: "6px",
              display: "block",
              color: "#333",
            }}
          >
            Select Date
          </label>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="dd MMMM yyyy"
            className="form-control"
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ textAlign: "right" }}>
          <label
            style={{
              fontWeight: "bold",
              marginBottom: "6px",
              display: "block",
              color: "#333",
            }}
          >
            Filter By
          </label>
          <ButtonGroup>
            {filterOptions.map((option) => (
              <Button
                key={option}
                variant={
                  selectedFilter === option ? "primary" : "outline-primary"
                }
                onClick={() => setSelectedFilter(option)}
                style={{
                  fontWeight: selectedFilter === option ? "bold" : "normal",
                }}
              >
                {filterLabels[option]}
              </Button>
            ))}
          </ButtonGroup>
        </div>
      </div>

      {/* Global Loading Display */}
      {(loading.maya || loading.jiffy) && (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            marginBottom: "20px",
            boxShadow: "0 0 6px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ fontSize: "18px", color: "#666" }}>
            📊 Loading production data...
          </div>
          <div style={{ fontSize: "14px", color: "#999", marginTop: "10px" }}>
            {loading.maya && "Loading Maya data... "}
            {loading.jiffy && "Loading Jiffy data..."}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs
        defaultActiveKey="maya"
        id="production-tabs"
        className="mb-4"
        style={{
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 0 6px rgba(0,0,0,0.05)",
        }}
      >
        {/* Maya Tab */}
        <Tab eventKey="maya" title="🎨 Maya">
          <div style={{ padding: "10px" }}>
            <h5 className="mb-3" style={{ color: "#333", fontWeight: "bold" }}>
              🖨️ Printer Summary
            </h5>

            <PrinterChart
              data={mayaPrinterData}
              loading={loading.maya}
              error={error.maya}
            />

            <StatisticsSummary
              printerData={mayaPrinterData}
              orderStats={mayaOrderStats}
              isJiffy={false}
            />
          </div>
        </Tab>

        {/* Jiffy Tab */}
        <Tab eventKey="jiffy" title="🚀 Jiffy">
          <div style={{ padding: "10px" }}>
            <h5 className="mb-3" style={{ color: "#333", fontWeight: "bold" }}>
              🖨️ Jiffy Printer Summary
            </h5>

            <PrinterChart
              data={jiffyPrinterData}
              loading={loading.jiffy}
              error={error.jiffy}
            />

            <StatisticsSummary
              printerData={jiffyPrinterData}
              orderStats={jiffyOrderStats}
              isJiffy={true}
              tableStats={jiffyTableStats}
              orderTypeStats={jiffyOrderTypeStats}
              printTypeStats={jiffyPrintTypeStats}
            />
          </div>
        </Tab>
      </Tabs>

      {/* Footer Info */}
      <div
        style={{
          textAlign: "center",
          marginTop: "30px",
          color: "#999",
          fontSize: "14px",
        }}
      >
        <p>
          Data last updated: {new Date().toLocaleString()} | Filter:{" "}
          {filterLabels[selectedFilter]} | Date Range:{" "}
          {selectedDate.toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export default ProductionStatsPage;
